// src/core/parallax.js

export function MotionFlowParallax(userOptions = {}) {
  let items = [];
  let latestScrollY = typeof window !== "undefined" ? window.scrollY : 0;
  let ticking = false;
  let initialized = false;
  let rafId = null;

  let scrollHandler = null;
  let resizeHandler = null;

  /* ==========================================================
     ACCESSIBILITY
  ========================================================== */
  const reduceMotion =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  /* ==========================================================
     INTERNAL TUNING
  ========================================================== */
  const INTENSITY = 0.1;
  const BUFFER_RATIO = 0.3;
  const SMOOTH = window.innerWidth < 768 ? 0.14 : 0.1;

  /* ==========================================================
     DEFAULTS
  ========================================================== */
  const defaults = {
    speed: 1,
    tabletSpeed: 0,
    mobileSpeed: 0,

    breakpoints: {
      mobile: 768,
      tablet: 1024,
    },

    stagger: {
      speed: 0,
      tabletSpeed: 0,
      mobileSpeed: 0,

      step: 0.3,
      tabletStep: 0.2,
      mobileStep: 0.1,

      direction: "left",
    },
  };

  const cfg = {
    ...defaults,
    ...(userOptions.parallax || {}),

    breakpoints: {
      ...defaults.breakpoints,
      ...((userOptions.parallax && userOptions.parallax.breakpoints) || {}),
    },

    stagger: {
      ...defaults.stagger,
      ...((userOptions.parallax && userOptions.parallax.stagger) || {}),
    },
  };

  /* ==========================================================
     HELPERS
  ========================================================== */
  function lerp(a, b, n) {
    return a + (b - a) * n;
  }

  function getSpeedForDevice(el) {
    if (el.dataset.mfParallaxLocked === "true") {
      const s = parseFloat(el.dataset.mfParallaxSpeed);
      return Number.isFinite(s) ? s : 0;
    }

    const w = window.innerWidth;

    let s =
      w <= cfg.breakpoints.mobile && el.dataset.mfParallaxSpeedMobile != null
        ? parseFloat(el.dataset.mfParallaxSpeedMobile)
        : w <= cfg.breakpoints.tablet &&
          el.dataset.mfParallaxSpeedTablet != null
        ? parseFloat(el.dataset.mfParallaxSpeedTablet)
        : el.dataset.mfParallaxSpeed != null
        ? parseFloat(el.dataset.mfParallaxSpeed)
        : w <= cfg.breakpoints.mobile
        ? cfg.mobileSpeed
        : w <= cfg.breakpoints.tablet
        ? cfg.tabletSpeed
        : cfg.speed;

    return Number.isFinite(s) ? s : 0;
  }

  function getStaggerBaseSpeed(parent) {
    const w = window.innerWidth;

    let s =
      w <= cfg.breakpoints.mobile &&
      parent.dataset.mfParallaxStaggerSpeedMobile != null
        ? parseFloat(parent.dataset.mfParallaxStaggerSpeedMobile)
        : w <= cfg.breakpoints.tablet &&
          parent.dataset.mfParallaxStaggerSpeedTablet != null
        ? parseFloat(parent.dataset.mfParallaxStaggerSpeedTablet)
        : parent.dataset.mfParallaxStaggerSpeed != null
        ? parseFloat(parent.dataset.mfParallaxStaggerSpeed)
        : w <= cfg.breakpoints.mobile
        ? cfg.stagger.mobileSpeed
        : w <= cfg.breakpoints.tablet
        ? cfg.stagger.tabletSpeed
        : cfg.stagger.speed;

    return Number.isFinite(s) ? s : 0;
  }

  function getStaggerStep(parent) {
    const w = window.innerWidth;

    let s =
      w <= cfg.breakpoints.mobile &&
      parent.dataset.mfParallaxStaggerStepMobile != null
        ? parseFloat(parent.dataset.mfParallaxStaggerStepMobile)
        : w <= cfg.breakpoints.tablet &&
          parent.dataset.mfParallaxStaggerStepTablet != null
        ? parseFloat(parent.dataset.mfParallaxStaggerStepTablet)
        : parent.dataset.mfParallaxStaggerStep != null
        ? parseFloat(parent.dataset.mfParallaxStaggerStep)
        : w <= cfg.breakpoints.mobile
        ? cfg.stagger.mobileStep
        : w <= cfg.breakpoints.tablet
        ? cfg.stagger.tabletStep
        : cfg.stagger.step;

    return Number.isFinite(s) ? s : 0;
  }

  function getDocumentTop(el) {
    const rect = el.getBoundingClientRect();
    return rect.top + window.scrollY;
  }

  /* ==========================================================
     INIT
  ========================================================== */
  function init() {
    if (initialized || reduceMotion) return;
    initialized = true;

    items = [];
    ticking = false;

    /* ----------------------------------------------------------
       STAGGER PARALLAX
    ---------------------------------------------------------- */
    document.querySelectorAll("[data-mf-parallax-stagger]").forEach(parent => {
      const base = getStaggerBaseSpeed(parent);
      const step = getStaggerStep(parent);

      const dir = (
        parent.dataset.mfParallaxStaggerDirection ??
        cfg.stagger.direction
      ).toLowerCase();

      const children = Array.from(parent.children).filter(
        c => !c.hasAttribute("data-mf-parallax-stagger-ignore")
      );

      const n = children.length;
      if (!n) return;

      const centerIndex = Math.floor((n - 1) / 2);

      children.forEach((child, i) => {
        let mul = i;
        if (dir === "right") mul = n - 1 - i;
        if (dir === "center") mul = Math.abs(i - centerIndex);

        const speed = base + step * mul;
        const rounded = Math.round(speed * 1000) / 1000;

        child.dataset.mfParallax = "";
        child.dataset.mfParallaxSpeed = String(rounded);
        child.dataset.mfParallaxLocked = "true";
      });
    });

    /* ----------------------------------------------------------
       COLLECT ELEMENTS
    ---------------------------------------------------------- */
    document.querySelectorAll("[data-mf-parallax]").forEach(el => {
      if (el.hasAttribute("data-mf-parallax-ignore")) return;

      const speed = getSpeedForDevice(el);
      if (speed === 0) return;

      el.style.willChange = "transform";

      items.push({
        el,
        speed,
        docTop: getDocumentTop(el),
        currentY: 0,
        targetY: 0,
      });
    });

    if (!items.length) return;

    /* ----------------------------------------------------------
       UPDATE TARGETS
    ---------------------------------------------------------- */
    function updateTargets() {
      const scrollY = latestScrollY;
      const vh = window.innerHeight;
      const buffer = vh * BUFFER_RATIO;

      items.forEach(item => {
        const rect = item.el.getBoundingClientRect();
        if (rect.top > vh + buffer || rect.bottom < -buffer) return;

        const startScrollY = item.docTop - vh;
        const raw = scrollY - startScrollY;
        const active = Math.max(0, raw);

        item.targetY =
          -Math.round(active * item.speed * INTENSITY * 100) / 100;
      });

      if (!rafId) rafId = requestAnimationFrame(render);
      ticking = false;
    }

    /* ----------------------------------------------------------
       RENDER
    ---------------------------------------------------------- */
    function render() {
      let moving = false;

      items.forEach(item => {
        item.currentY = lerp(item.currentY, item.targetY, SMOOTH);
        if (Math.abs(item.currentY - item.targetY) > 0.1) moving = true;

        item.el.style.transform =
          `translate3d(0, ${item.currentY}px, 0)`;
      });

      rafId = moving ? requestAnimationFrame(render) : null;
    }

    /* ----------------------------------------------------------
       EVENTS
    ---------------------------------------------------------- */
    scrollHandler = () => {
      latestScrollY = window.scrollY;
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(updateTargets);
      }
    };

    resizeHandler = () => {
      latestScrollY = window.scrollY;

      items.forEach(item => {
        item.speed = getSpeedForDevice(item.el);
        item.docTop = getDocumentTop(item.el);
      });

      if (!ticking) {
        ticking = true;
        requestAnimationFrame(updateTargets);
      }
    };

    latestScrollY = window.scrollY;
    updateTargets();

    window.addEventListener("scroll", scrollHandler, { passive: true });
    window.addEventListener("resize", resizeHandler);
  }

  /* ==========================================================
     PUBLIC API
  ========================================================== */
  function refresh() {
    destroy(false);
    init();
  }

  function destroy(removeStyles = true) {
    initialized = false;

    window.removeEventListener("scroll", scrollHandler);
    window.removeEventListener("resize", resizeHandler);

    if (rafId) cancelAnimationFrame(rafId);
    rafId = null;

    if (removeStyles) {
      items.forEach(item => {
        item.el.style.transform = "";
        item.el.style.willChange = "";
      });
    }

    items = [];
    ticking = false;
  }

  /* ==========================================================
     START
  ========================================================== */
  init();

  return { refresh, destroy };
}
